/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import logger from '../../utils/logger';

// Credential Reset Dialog Component (PIN for students, Password for staff)
const EditPinDialog = ({ isOpen, onClose, user, onUpdatePin }) => {
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [minPinLength, setMinPinLength] = useState(4);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: 12,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSymbol: true
  });

  const isStaff = user?.role === 'teacher' || user?.role === 'admin' || user?.is_admin;

  useEffect(() => {
    if (!isOpen) return;
    // Reset state when dialog opens or user changes
    setNewPin('');
    setConfirmPin('');
    setError('');

    // Fetch config for validation
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.config?.security?.min_pin_length) {
            setMinPinLength(Number(data.config.security.min_pin_length));
          }
          if (data.config?.security?.password_min_length) {
            setPasswordRequirements({
              minLength: Number(data.config.security.password_min_length) || 12,
              requireUppercase: data.config.security.password_require_uppercase !== false && data.config.security.password_require_uppercase !== 'false',
              requireLowercase: data.config.security.password_require_lowercase !== false && data.config.security.password_require_lowercase !== 'false',
              requireNumber: data.config.security.password_require_number !== false && data.config.security.password_require_number !== 'false',
              requireSymbol: data.config.security.password_require_symbol !== false && data.config.security.password_require_symbol !== 'false'
            });
          }
        }
      } catch (err) {
        // Use defaults on failure
      }
    };
    fetchConfig();
  }, [isOpen, user]);

  const passwordStatus = {
    length: newPin.length >= passwordRequirements.minLength,
    uppercase: !passwordRequirements.requireUppercase || /[A-Z]/.test(newPin),
    lowercase: !passwordRequirements.requireLowercase || /[a-z]/.test(newPin),
    number: !passwordRequirements.requireNumber || /[0-9]/.test(newPin),
    symbol: !passwordRequirements.requireSymbol || /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPin),
    match: newPin.length > 0 && newPin === confirmPin
  };

  const handleSubmit = () => {
    if (newPin !== confirmPin) {
      setError(isStaff ? 'Passwords do not match' : 'PINs do not match');
      return;
    }

    if (isStaff) {
      const allMet = Object.values(passwordStatus).every(Boolean);
      if (!allMet) {
        setError('Please ensure all password requirements are met.');
        return;
      }
    } else {
      if (!newPin || newPin.length < minPinLength || !/^\d+$/.test(newPin)) {
        setError(`PIN must be at least ${minPinLength} numeric digits`);
        return;
      }
    }

    onUpdatePin(newPin);
  };

  const RequirementItem = ({ met, label }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-500'}`}>
      <span className={`inline-block w-4 text-center ${met ? 'text-green-600' : 'text-gray-400'}`}>
        {met ? '\u2713' : '\u2717'}
      </span>
      <span>{label}</span>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isStaff
              ? `Reset Password for ${user?.first_name} ${user?.last_name}`
              : `Update PIN for ${user?.first_name} ${user?.last_name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="newPin">{isStaff ? 'New Password' : 'New PIN'}</Label>
            <Input
              id="newPin"
              type="password"
              value={newPin}
              onChange={(e) => { setNewPin(e.target.value); setError(''); }}
              {...(!isStaff ? { inputMode: 'numeric', pattern: '[0-9]*', maxLength: minPinLength } : {})}
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label htmlFor="confirmPin">{isStaff ? 'Confirm Password' : 'Confirm PIN'}</Label>
            <Input
              id="confirmPin"
              type="password"
              value={confirmPin}
              onChange={(e) => { setConfirmPin(e.target.value); setError(''); }}
              {...(!isStaff ? { inputMode: 'numeric', pattern: '[0-9]*', maxLength: minPinLength } : {})}
              autoComplete="new-password"
            />
          </div>

          {isStaff && newPin.length > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg space-y-1">
              <p className="text-sm font-medium text-gray-700 mb-1">Password Requirements:</p>
              <RequirementItem met={passwordStatus.length} label={`At least ${passwordRequirements.minLength} characters`} />
              {passwordRequirements.requireUppercase && (
                <RequirementItem met={passwordStatus.uppercase} label="One uppercase letter (A-Z)" />
              )}
              {passwordRequirements.requireLowercase && (
                <RequirementItem met={passwordStatus.lowercase} label="One lowercase letter (a-z)" />
              )}
              {passwordRequirements.requireNumber && (
                <RequirementItem met={passwordStatus.number} label="One number (0-9)" />
              )}
              {passwordRequirements.requireSymbol && (
                <RequirementItem met={passwordStatus.symbol} label="One special character (!@#$%...)" />
              )}
              <RequirementItem met={passwordStatus.match} label="Passwords match" />
            </div>
          )}

          {!isStaff && (
            <p className="text-sm text-gray-500">PIN must be at least {minPinLength} numeric digits.</p>
          )}

          {isStaff && (
            <p className="text-sm text-gray-500">User will be required to change this on next login.</p>
          )}

          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit}>
            {isStaff ? 'Reset Password' : 'Update PIN'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Edit Dialog Component
const EditUserDialog = ({ isOpen, onClose, user, onUpdateUser, cohorts }) => {
  const [editedUser, setEditedUser] = useState(user || {});

  useEffect(() => {
    setEditedUser(user || {});
  }, [user]);

  const handleSubmit = () => {
    logger.info('user_edit_submit', {
      userId: editedUser.id,
      role: editedUser.role,
      updatedFields: Object.keys(editedUser)
    });
    onUpdateUser(editedUser);
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedUser(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User: {editedUser.first_name} {editedUser.last_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Basic Information */}
          <div>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              name="first_name"
              value={editedUser.first_name || ''}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              name="last_name"
              value={editedUser.last_name || ''}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label htmlFor="student_id">Student ID</Label>
            <Input
              id="student_id"
              name="student_id"
              value={editedUser.student_id || ''}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={editedUser.email || ''}
              onChange={handleInputChange}
              placeholder="student@school.edu"
            />
            <p className="text-xs text-gray-500 mt-1">
              Required for secure PIN reset functionality
            </p>
          </div>
          
          {/* Role Selection */}
          <div className="space-y-2">
            <Label htmlFor="role">User Role</Label>
            <Select
              value={editedUser.role || 'student'}
              onValueChange={(value) => setEditedUser(prev => ({ 
                ...prev, 
                role: value,
                is_admin: value === 'admin',
                // Clear shop_class if becoming teacher
                shop_class: value === 'teacher' ? null : prev.shop_class,
                // Initialize assignedClasses if becoming teacher
                assignedClasses: value === 'teacher' ? [] : null
              }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="teacher">Teacher</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Student Class Assignments */}
          {editedUser.role === 'student' && (
            <div className="space-y-2">
              <Label>Assigned Classes</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                    <Checkbox
                      id={`student-cohort-${cohort.id}`}
                      checked={editedUser.assignedClasses?.includes(cohort.id)}
                      onCheckedChange={(checked) => {
                        const updatedClasses = checked 
                          ? [...(editedUser.assignedClasses || []), cohort.id]
                          : (editedUser.assignedClasses || []).filter(id => id !== cohort.id);
                        setEditedUser(prev => ({ 
                          ...prev, 
                          assignedClasses: updatedClasses,
                          shop_class: updatedClasses.length > 0 ? cohorts.find(c => c.id === updatedClasses[0])?.name : ''
                        }));
                      }}
                    />
                    <Label htmlFor={`student-cohort-${cohort.id}`} className="cursor-pointer">
                      {cohort.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Role - Single Class Selection */}
          {editedUser.role === 'admin' && (
            <div>
              <Label htmlFor="shop_class">Shop Class</Label>
              <Select
                value={editedUser.shop_class || ''}
                onValueChange={(value) => setEditedUser(prev => ({ ...prev, shop_class: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a shop class" />
                </SelectTrigger>
                <SelectContent>
                  {cohorts.map((cohort) => (
                    <SelectItem key={cohort.id} value={cohort.name}>
                      {cohort.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Teacher Class Assignments */}
          {editedUser.role === 'teacher' && (
            <div className="space-y-2">
              <Label>Assigned Classes</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                    <Checkbox
                      id={`cohort-${cohort.id}`}
                      checked={editedUser.assignedClasses?.includes(cohort.id)}
                      onCheckedChange={(checked) => {
                        const updatedClasses = checked 
                          ? [...(editedUser.assignedClasses || []), cohort.id]
                          : (editedUser.assignedClasses || []).filter(id => id !== cohort.id);
                        setEditedUser(prev => ({ 
                          ...prev, 
                          assignedClasses: updatedClasses 
                        }));
                      }}
                    />
                    <Label htmlFor={`cohort-${cohort.id}`} className="cursor-pointer">
                      {cohort.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit}>Update User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main UserManagement Component
const CreateUserDialog = ({ isOpen, onClose, onCreateUser, cohorts }) => {
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    student_id: '',
    role: 'student',
    pin: '',
    shop_class: '',
    assignedClasses: []
  });
  const [validationError, setValidationError] = useState('');
  const [minPinLength, setMinPinLength] = useState(4);

  useEffect(() => {
    if (!isOpen) return;
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.config?.security?.min_pin_length) {
            setMinPinLength(Number(data.config.security.min_pin_length));
          }
        }
      } catch (err) { /* use default */ }
    };
    fetchConfig();
  }, [isOpen]);

  const validateForm = () => {
    if (!newUser.first_name || !newUser.last_name || !newUser.student_id) {
      setValidationError('Please fill in all required fields');
      return false;
    }
    if (!newUser.pin || newUser.pin.length < minPinLength || !/^\d+$/.test(newUser.pin)) {
      setValidationError(`PIN must be at least ${minPinLength} numeric digits`);
      return false;
    }
    if (newUser.role === 'student' && newUser.assignedClasses.length === 0) {
      setValidationError('Students must be assigned to at least one class');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onCreateUser(newUser);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="student_id">ID Number</Label>
            <Input
              id="student_id"
              value={newUser.student_id}
              onChange={(e) => setNewUser({ ...newUser, student_id: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="pin">PIN (min {minPinLength} digits)</Label>
            <Input
              id="pin"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={minPinLength}
              value={newUser.pin}
              onChange={(e) => setNewUser({ ...newUser, pin: e.target.value.replace(/\D/g, '') })}
              required
            />
          </div>

          <div>
            <Label htmlFor="role">User Role</Label>
            <Select
              value={newUser.role}
              onValueChange={(value) => setNewUser({ 
                ...newUser, 
                role: value,
                shop_class: value === 'teacher' ? '' : newUser.shop_class,
                assignedClasses: value === 'teacher' ? [] : null
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="teacher">Teacher</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Student Class Assignments */}
          {newUser.role === 'student' && (
            <div className="space-y-2">
              <Label>Assigned Classes</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                    <Checkbox
                      id={`new-student-cohort-${cohort.id}`}
                      checked={newUser.assignedClasses.includes(cohort.id)}
                      onCheckedChange={(checked) => {
                        setNewUser(prev => ({
                          ...prev,
                          assignedClasses: checked
                            ? [...prev.assignedClasses, cohort.id]
                            : prev.assignedClasses.filter(id => id !== cohort.id),
                          shop_class: checked && prev.assignedClasses.length === 0 
                            ? cohort.name 
                            : prev.shop_class
                        }));
                      }}
                    />
                    <Label htmlFor={`new-student-cohort-${cohort.id}`} className="cursor-pointer">
                      {cohort.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Role - Single Class Selection */}
          {newUser.role === 'admin' && (
            <div>
              <Label htmlFor="shop_class">Shop Class</Label>
              <Select
                value={newUser.shop_class}
                onValueChange={(value) => setNewUser({ ...newUser, shop_class: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a shop class" />
                </SelectTrigger>
                <SelectContent>
                  {cohorts.map((cohort) => (
                    <SelectItem key={cohort.id} value={cohort.name}>
                      {cohort.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {newUser.role === 'teacher' && (
            <div className="space-y-2">
              <Label>Assigned Classes</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50">
                    <Checkbox
                      id={`cohort-${cohort.id}`}
                      checked={newUser.assignedClasses.includes(cohort.id)}
                      onCheckedChange={(checked) => {
                        setNewUser(prev => ({
                          ...prev,
                          assignedClasses: checked
                            ? [...prev.assignedClasses, cohort.id]
                            : prev.assignedClasses.filter(id => id !== cohort.id)
                        }));
                      }}
                    />
                    <Label htmlFor={`cohort-${cohort.id}`} className="cursor-pointer">
                      {cohort.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {validationError && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleSubmit}>Create User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [editingPinUser, setEditingPinUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchCohorts();
  }, []);

  // Get user's assigned classes when fetching users
const fetchUsers = async () => {
  try {
  setLoading(true);
  const [usersResponse, teacherAssignmentsResponse] = await Promise.all([
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`),
    fetch(`${process.env.REACT_APP_API_URL}/api/admin/teacher-assignments`)
  ]);

    if (!usersResponse.ok) throw new Error('Failed to fetch users');
  const userData = await usersResponse.json();

    // If teacher assignments request succeeded, merge with user data
  if (teacherAssignmentsResponse.ok) {
      const assignmentsData = await teacherAssignmentsResponse.json();
      const usersWithAssignments = userData.map(user => ({
        ...user,
        assignedClasses: assignmentsData[user.id] || []
      }));
      setUsers(usersWithAssignments);
    } else {
      setUsers(userData);
    }

    logger.info('users_fetched_successfully', { count: userData.length });
  } catch (error) {
    logger.error('user_fetch_failed', {}, error);
    setError('Failed to load users. Please try again.');
  } finally {
    setLoading(false);
  }
};

  const fetchCohorts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cohorts`);
      if (!response.ok) throw new Error('Failed to fetch cohorts');
      const data = await response.json();
      setCohorts(data);
      logger.info('cohorts_fetched_successfully', { count: data.length });
    } catch (error) {
      logger.error('cohorts_fetch_failed', {}, error);
      setError('Failed to load shop classes. Please try again.');
    }
  };

  // User Management Handlers
const handleCreateUser = async (userData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      logger.info('user_created_successfully', {
        role: userData.role,
        hasAssignedClasses: userData.role === 'teacher' && userData.assignedClasses?.length > 0
      });

      await fetchUsers(); // Refresh user list
      setError('User created successfully');
      setIsCreatingUser(false);
    } catch (error) {
      logger.error('user_creation_failed', {
        error: error.message
      });
      setError('Failed to create user: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setDeleteLoading(userId);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`, { 
          method: 'DELETE' 
        });
        
        if (!response.ok) throw new Error('Failed to delete user');
        
        logger.info('user_deleted_successfully', { userId });
        await fetchUsers();
        setError('User deleted successfully.');
      } catch (error) {
        logger.error('user_deletion_failed', { userId }, error);
        setError('Failed to delete user. Please try again.');
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  const handleUpdatePin = async (newPin) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${editingPinUser.id}/updatePin`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newPin })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update credential');
      }

      logger.info('credential_updated_successfully', { userId: editingPinUser.id });
      setError(data.message || 'Credential updated successfully');
      setEditingPinUser(null);
      await fetchUsers();
    } catch (error) {
      logger.error('credential_update_failed', { userId: editingPinUser.id }, error);
      setError(error.message || 'Failed to update credential. Please try again.');
    }
  };

  const handleUpdateUser = async (updatedUser) => {
    try {
      const payload = {
        ...updatedUser,
        assigned_classes: updatedUser.role === 'teacher' || updatedUser.role === 'student'
          ? (Array.isArray(updatedUser.assignedClasses)
            ? updatedUser.assignedClasses
            : [])
        : null,
        is_admin: updatedUser.role === 'admin'
      };

      logger.debug('user_update_payload', {
        userId: updatedUser.id,
        role: updatedUser.role,
        assignedClasses: payload.assigned_classes
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${updatedUser.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update user');
    }

      logger.info('user_update_success', {
        userId: updatedUser.id,
        role: updatedUser.role,
        assignedClassesCount: payload.assigned_classes?.length || 0
      });

      setError('User updated successfully');
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      logger.error('user_update_failed', { 
        userId: updatedUser.id,
        error: error.message
      });
      setError('Failed to update user. Please try again.');
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (user.first_name && user.first_name.toLowerCase().includes(searchTermLower)) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchTermLower)) ||
      (user.student_id && String(user.student_id).toLowerCase().includes(searchTermLower)) ||
      (user.shop_class && user.shop_class.toLowerCase().includes(searchTermLower))
    );
  });

  if (loading) return <p>Loading users...</p>;

  // Main Render
  return (
    <div>
      {error && (
        <Alert variant={error.includes('successfully') ? 'default' : 'destructive'}>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Input
        type="text"
        placeholder="Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="mb-4"
      />
      <Button onClick={() => setIsCreatingUser(true)}>
        Create New User
      </Button>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Student ID</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Shop Class</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.first_name} {user.last_name}</TableCell>
              <TableCell>{user.student_id}</TableCell>
              <TableCell>
                {user.email ? (
                  <span className="text-sm">{user.email}</span>
                ) : (
                  <span className="text-xs text-red-500 italic">No email</span>
                )}
              </TableCell>
              <TableCell>{user.role || 'student'}</TableCell>
              <TableCell>{user.shop_class}</TableCell>
              <TableCell>
                <Button 
                  onClick={() => setEditingPinUser(user)} 
                  variant="outline"
                  className="mr-2"
                >
                  Edit PIN
                </Button>
                <Button 
                  onClick={() => setEditingUser({
                    ...user,
                    assignedClasses: user.assigned_classes || []
                  })} 
                  variant="outline"
                  className="mr-2"
                >
                  Edit User
                </Button>
                <Button 
                  onClick={() => handleDeleteUser(user.id)} 
                  variant="destructive"
                  disabled={deleteLoading === user.id}
                >
                  {deleteLoading === user.id ? 'Deleting...' : 'Delete'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isCreatingUser && (
        <CreateUserDialog
          isOpen={true}
          onClose={() => setIsCreatingUser(false)}
          onCreateUser={handleCreateUser}
          cohorts={cohorts}
        />
      )}

      {editingPinUser && (
        <EditPinDialog
          isOpen={true}
          onClose={() => setEditingPinUser(null)}
          user={editingPinUser}
          onUpdatePin={handleUpdatePin}
        />
      )}

      {editingUser && (
        <EditUserDialog
          isOpen={true}
          onClose={() => setEditingUser(null)}
          user={editingUser}
          onUpdateUser={handleUpdateUser}
          cohorts={cohorts}
        />
      )}
    </div>
  );
};

export default UserManagement;